import { BigNumber } from 'bignumber.js';
import { createContext, useCallback, useContext, useMemo } from 'react';

import { useLocalStorage } from '@infinex/ui-utils/storage';

import type { CurrencyUnit } from './types';

import {
  formatTokenValue,
  keepAtMost2Decimals,
} from '../swidge/utils/formatSwidgeTokenValue';

/** The user's persisted display-unit preference, shared across all flows. */
export function useCurrencyUnitPreference() {
  return useLocalStorage<CurrencyUnit>({
    key: 'unit-switch/unit',
    initialValue: 'token',
  });
}

interface CurrencyUnitContext {
  isEnabled: boolean;
  unit: CurrencyUnit;
  updateUnit: (nextUnit: CurrencyUnit, value?: string) => void;

  tokenValue: BigNumber | null;
  usdValue: number | null;
  inputAmountInTokenUnit: string | null;
}

const CurrencyUnitContextInstance = createContext<CurrencyUnitContext | null>(
  null
);

type CurrencyUnitProviderProps = {
  children: React.ReactNode;
  isEnabled?: boolean;
  onFormValueUpdate: (value: string) => void;

  selectedTokenUsdRate: number | null;
  selectedTokenDecimals: number | null;

  /**
   * This amount is the user's input amount.
   * It can be the token amount or the USD amount, depending on the unit.
   */
  inputAmount: string | null;
};

export function CurrencyUnitProvider(props: CurrencyUnitProviderProps) {
  const {
    isEnabled = true,
    children,
    onFormValueUpdate,
    selectedTokenDecimals,
    inputAmount,
    selectedTokenUsdRate,
  } = props;

  const [_unit, setUnit] = useCurrencyUnitPreference();

  let unit = _unit;
  if (!isEnabled) {
    unit = 'token';
  }

  let tokenValue: BigNumber | null = null;
  let usdValue: number | null = null;
  if (selectedTokenUsdRate) {
    if (unit === 'token') {
      tokenValue = BigNumber(inputAmount || 0);
      usdValue = tokenValue.multipliedBy(selectedTokenUsdRate).toNumber();
    } else {
      // when unit is usd, we don't need to calculate the usd value
      usdValue = Number(inputAmount ?? 0);
      tokenValue = BigNumber(usdValue).dividedBy(selectedTokenUsdRate);
    }
  } else if (unit === 'token') {
    // No price data available — still compute tokenValue so the send flow can proceed
    tokenValue = inputAmount ? BigNumber(inputAmount) : null;
  }

  const handleUpdateUnit = useCallback(
    (nextUnit: CurrencyUnit, value?: string) => {
      const currentUnit = unit;
      if (nextUnit === currentUnit && !value) {
        return;
      }

      if (value) {
        onFormValueUpdate(value);
      } else {
        let nextValue = '';
        if (nextUnit === 'token') {
          // format the token value with the same rules
          // so that when we swap, the value are the same
          nextValue = formatTokenValue(
            tokenValue ?? BigNumber(0),
            selectedTokenUsdRate,
            selectedTokenDecimals ?? 6
          ).realNotation.replace(/,/g, '');
        } else {
          nextValue = keepAtMost2Decimals(usdValue);
        }

        if (Number(nextValue) === 0) {
          setUnit(nextUnit);
          return;
        }

        onFormValueUpdate(nextValue);
      }

      setUnit(nextUnit);
    },
    [
      unit,
      setUnit,
      onFormValueUpdate,
      tokenValue,
      selectedTokenUsdRate,
      selectedTokenDecimals,
      usdValue,
    ]
  );

  const contextValue = useMemo(() => {
    if (!isEnabled) {
      return {
        isEnabled: false,
        unit: 'token' as CurrencyUnit,
        updateUnit: (_nextUnit: CurrencyUnit, _value?: string) => {},
        tokenValue,
        usdValue,
        inputAmountInTokenUnit: inputAmount,
      };
    }

    return {
      isEnabled: true,
      unit: unit || 'token',
      updateUnit: handleUpdateUnit,
      tokenValue,
      usdValue,
      inputAmountInTokenUnit:
        unit === 'token' ? inputAmount : (tokenValue?.toString() ?? null),
    };
  }, [isEnabled, unit, handleUpdateUnit, tokenValue, usdValue, inputAmount]);

  return (
    <CurrencyUnitContextInstance.Provider value={contextValue}>
      {children}
    </CurrencyUnitContextInstance.Provider>
  );
}

export function useCurrencyUnit() {
  const context = useContext(CurrencyUnitContextInstance);
  if (!context) {
    throw new Error('useCurrencyUnit must be used within CurrencyUnitProvider');
  }
  return context;
}
